/**
 * Each server's own settings, which live on the media_server and come through the relay, and
 * the addresses its network interfaces hold.
 *
 * A `servers` change frame reloads the settings of the server it names. The cloud logs one
 * when the settings change: from this browser, from another session, or on the server itself.
 * The addresses are reference data, so a frame leaves them.
 */

import { atom, type WritableAtom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import type { ServerAddressesSnapshot, ServerSettingsSnapshot } from '@/types/servers';
import { SnapshotCommand, type SnapshotWrite } from '@/types/snapshot';

const NO_SETTINGS: ServerSettingsSnapshot = { settings: null, isLoading: true, error: null };

/** One server's settings: read it for the snapshot, set it with a command. */
export const serverSettingsAtom = atomFamily((serverId: string) => snapshotMemberAtom(
    snapshotAtoms<ServerSettingsSnapshot>(NO_SETTINGS, async (): Promise<
        ServerSettingsSnapshot
    > => ({ settings: await api.getServerSettings(serverId), isLoading: false, error: null })),
));

const NO_ADDRESSES: ServerAddressesSnapshot = { addresses: null, isLoading: true, error: null };

/** One server's local addresses: read it for the snapshot, set it with a command. */
export const serverAddressesAtom = atomFamily((serverId: string) => snapshotMemberAtom(
    snapshotAtoms<ServerAddressesSnapshot>(NO_ADDRESSES, async (): Promise<
        ServerAddressesSnapshot
    > => ({
        addresses: await api.getServerLocalAddresses(serverId), isLoading: false, error: null,
    })),
));

const OFFLINE_SETTINGS: ServerSettingsSnapshot = { settings: null, isLoading: false, error: null };
const OFFLINE_ADDRESSES: ServerAddressesSnapshot = {
    addresses: null, isLoading: false, error: null,
};

/** What a form reads while its server is offline: the relay cannot reach it, so none loads. */
export const offlineSettingsAtom: WritableAtom<
    ServerSettingsSnapshot, [SnapshotWrite<ServerSettingsSnapshot>], void
> = atom(
    (): ServerSettingsSnapshot => OFFLINE_SETTINGS,
    (_get, _set, _write: SnapshotWrite<ServerSettingsSnapshot>): void => undefined,
);

export const offlineAddressesAtom: WritableAtom<
    ServerAddressesSnapshot, [SnapshotWrite<ServerAddressesSnapshot>], void
> = atom(
    (): ServerAddressesSnapshot => OFFLINE_ADDRESSES,
    (_get, _set, _write: SnapshotWrite<ServerAddressesSnapshot>): void => undefined,
);

const RELOAD = { command: SnapshotCommand.Reload } as const;

/** What the notifications stream means for the servers' own settings. */
export const serverSettingsStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    // The gap may have hidden a change frame, and the interfaces may have changed in it.
    if (input.kind === StreamInputKind.Reopened) {
        for (const serverId of serverSettingsAtom.getParams()) {
            set(serverSettingsAtom(serverId), RELOAD);
        }
        for (const serverId of serverAddressesAtom.getParams()) {
            set(serverAddressesAtom(serverId), RELOAD);
        }
        return;
    }
    if (input.kind !== StreamInputKind.Frame) {
        return;
    }
    const { frame } = input;
    if (frame.type !== 'change' || frame.resource !== 'servers') {
        return;
    }
    // Only an open form has a member.
    if ([...serverSettingsAtom.getParams()].includes(frame.id)) {
        set(serverSettingsAtom(frame.id), RELOAD);
    }
});
